/**
 * OnboardingLauncher — the Project Onboarding tool (team-only): the guided checklist that gets a
 * newly-executed project fully set up in BQE Core. Direct port of the legacy Softr wizard
 * (examples/opportunity_details.html buildOnboardingCard/buildBSOnboardingCard/buildOverlay) —
 * see docs/PROJECT_ONBOARDING.md for the full logic record and the deliberate deviations.
 *
 * Three surfaces, all driven by the project's `Project Onboarding` record (created by the
 * execution-time Airtable automation — this component never creates one; no record = renders
 * nothing at all):
 *   1. Full-screen "PROJECT READY / Begin Onboarding" overlay — first time anyone opens the
 *      project after execution (Project.'Onboarding Acknowledged' unset). Clicking Begin stamps
 *      the flag (once per project, not per person) and opens the wizard. A "BASIC SERVICES
 *      ACTIVATED" variant fires for the DC→BS second cycle ('BS Onboarding Active' set,
 *      'BS Onboarding Acknowledged' unset).
 *   2. A persistent bottom-right pill ("Project Onboarding — Section X of Y") while any cycle is
 *      incomplete, so the checklist stays visible without re-interrupting.
 *   3. The wizard modal itself — numbered gated sections; each section's action button confirms,
 *      writes its Airtable step flag, and advances.
 *
 * Cycle/track resolution (ported exactly):
 *   hasDC = Project.'Executed DC Proposal' non-empty. bsOnlyMode = !hasDC (straight-to-BS
 *   projects run the whole FIRST cycle on the BS* fields — 'BS Ready for Core',
 *   'BS Phases Verified', 'BS Retainer Required', 'BS Send Retainer Request',
 *   'BS Onboarding Complete' — and skip the client-verification section entirely).
 *   secondCycle = 'BS Onboarding Active' (a DC-track project whose BS proposal later executed).
 *
 * Mounted once in OutlineView. Only renders when zoomed into a project (wf.zoomProjectId).
 */
import { useEffect, useReducer, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import {
  getOnboardingBundle, updateOnboarding, acknowledgeOnboarding, completeOnboarding, scaffoldProject,
} from '../services/onboarding.js'
import { getTeamMembers } from '../services/members.js'
import { getProposalDetail } from '../services/proposals.js'
import { confirmDialog, alertDialog } from './Dialog.jsx'

/* ── Shared derivations (ported from the legacy card) ─────────────────────── */

function first(v) { return Array.isArray(v) ? v[0] : v }
function money(n) { return n ? '$' + Math.round(n).toLocaleString() : '' }

function deriveState(bundle) {
  const pf = bundle.project?.fields || {}
  const onb = bundle.onboarding
  const onbF = onb?.fields || {}
  // A project that's already posted at least one deliverable is unambiguously in production —
  // its onboarding was handled somehow (usually the legacy Softr tool, which tracked completion
  // via its own 'Ready to Complete' flag rather than this tool's 'Onboarding Complete'/'BS
  // Onboarding Complete' checkboxes), even if those specific checkboxes were never actually
  // checked. Without this guard, every such pre-existing project resurfaces the "Begin
  // Onboarding" overlay/pill indefinitely, since firstCycleComplete below reads a checkbox the
  // legacy process never set. Real-world confirmed cases (2026-07): Ridgeway ADU, Sunday Goods
  // Show Low, Knapp Residence, Verheyen Residence — all long executed and in production, all
  // missing the checkbox.
  const alreadyInProduction = (Number(pf['✔️ Posted Deliverables Count']) || 0) > 0
  const hasDC = Array.isArray(pf['Executed DC Proposal']) && pf['Executed DC Proposal'].length > 0
  const bsOnlyMode = !hasDC
  const secondCycle = !!onbF['BS Onboarding Active']

  const isExisting = onbF['Existing Client'] === 'Yes'
  const needRetainer = bsOnlyMode ? onbF['BS Retainer Required'] === 'Yes' : onbF['DC Retainer Required'] === 'Yes'
  const readyForCore = bsOnlyMode ? !!onbF['BS Ready for Core'] : !!onbF['Ready for Core Phases']
  const clientDone = !!onbF['Add Client to BQE Core']
  const projDone = bsOnlyMode ? !!onbF['BS Phases Verified'] : !!onbF['Add Project to BQE Core']
  const retainerDone = bsOnlyMode ? !!onbF['BS Send Retainer Request'] : !!onbF['DC Send Retainer Request']
  const firstCycleComplete = bsOnlyMode ? !!onbF['BS Onboarding Complete'] : !!onbF['Onboarding Complete']
  const bsCycleComplete = !!onbF['BS Onboarding Complete']

  // Active-section resolver. The legacy page had a SEPARATE, hardcoded bsOnlyMode formula here
  // that never accounted for isExisting/clientDone at all — meaning a brand-new client on the
  // straight-to-BS track (e.g. Grabowski) never got a "Verify New Client in Core" step, even
  // though scaffold-project.js genuinely creates one. That formula's own non-bsOnlyMode branch
  // already only depends on isExisting/clientDone/projDone/needRetainer/retainerDone/
  // firstCycleComplete — none of which are DC-specific — so it's correct for BOTH tracks
  // unmodified; fixed by dropping the bsOnlyMode special-case rather than porting the gap.
  let activeSection = 1
  if (readyForCore && !isExisting && !clientDone) activeSection = 2
  else if (readyForCore && (isExisting || clientDone) && !projDone) activeSection = isExisting ? 2 : 3
  else if (projDone && needRetainer && !retainerDone) activeSection = isExisting ? 3 : 4
  else if (projDone && (!needRetainer || retainerDone) && !firstCycleComplete) activeSection = isExisting ? (needRetainer ? 4 : 3) : (needRetainer ? 5 : 4)
  else if (firstCycleComplete) activeSection = 99

  const totalSections = isExisting ? (needRetainer ? 4 : 3) : (needRetainer ? 5 : 4)

  // Second-cycle (DC→BS) sections
  const bsReady = !!onbF['BS Ready for Core']
  const bsProjDone = !!onbF['BS Phases Verified']
  const needBSRetainer = onbF['BS Retainer Required'] === 'Yes'
  const bsRetainerDone = !!onbF['BS Send Retainer Request']
  let bsActiveSection = 1
  if (bsReady && !bsProjDone) bsActiveSection = 2
  else if (bsProjDone && needBSRetainer && !bsRetainerDone) bsActiveSection = 3
  else if (bsProjDone && (!needBSRetainer || bsRetainerDone) && !bsCycleComplete) bsActiveSection = needBSRetainer ? 4 : 3
  else if (bsCycleComplete) bsActiveSection = 99
  const bsTotalSections = needBSRetainer ? 4 : 3

  const needFirstCycle = !!onb && !firstCycleComplete && !secondCycle
  const needBSCycle = !!onb && secondCycle && !bsCycleComplete

  return {
    pf, onbF, onbId: onb?.id || null, hasDC, bsOnlyMode, secondCycle,
    isExisting, needRetainer, readyForCore, clientDone, projDone, retainerDone,
    firstCycleComplete, bsCycleComplete, activeSection, totalSections,
    bsReady, bsProjDone, needBSRetainer, bsRetainerDone, bsActiveSection, bsTotalSections,
    needFirstCycle, needBSCycle, alreadyInProduction,
    anyIncomplete: (needFirstCycle || needBSCycle) && !alreadyInProduction,
  }
}

/* ── Launcher (overlay + pill + modal host) ───────────────────────────────── */

export default function OnboardingLauncher() {
  const [, forceUpdate] = useReducer(x => x + 1, 0)
  const [modalOpen, setModalOpen] = useState(false)
  const bundles = useRef({})   // projectId -> { status: 'loading'|'ready', data }
  const projectId = wf.zoomProjectId || null

  useEffect(() => {
    const onRender = () => forceUpdate()
    window.addEventListener('outline-render', onRender)
    window.addEventListener('popstate', onRender)
    return () => {
      window.removeEventListener('outline-render', onRender)
      window.removeEventListener('popstate', onRender)
    }
  }, [])

  function load(pid, { force = false } = {}) {
    if (!pid) return
    if (!force && bundles.current[pid]) return
    bundles.current[pid] = { status: 'loading', data: null }
    getOnboardingBundle(pid)
      .then(data => { bundles.current[pid] = { status: 'ready', data }; forceUpdate() })
      .catch(() => { bundles.current[pid] = { status: 'ready', data: null }; forceUpdate() })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (projectId) load(projectId) }, [projectId])

  if (!projectId) return null
  const entry = bundles.current[projectId]
  if (!entry || entry.status !== 'ready' || !entry.data || !entry.data.onboarding) return null

  const st = deriveState(entry.data)
  if (!st.anyIncomplete) return null

  const refresh = () => load(projectId, { force: true })

  // A full-bleed workspace (Billing/CRM/Pulse/… — ?ws= URL param, z-400 fixed panels) covers the
  // outline; don't interrupt on top of it. The pill also sits BELOW the workspace z (390 < 400)
  // so an open workspace naturally hides it.
  const wsOpen = !!new URLSearchParams(window.location.search).get('ws')

  // Overlay: interrupt once per project per cycle (acknowledged flags live on the Project).
  const showFirstOverlay = st.needFirstCycle && !st.pf['Onboarding Acknowledged'] && !modalOpen && !wsOpen
  const showBSOverlay = st.needBSCycle && !st.pf['BS Onboarding Acknowledged'] && !modalOpen && !wsOpen
  const projName = st.pf['Project Name'] || ''
  const projNum = st.pf['Project Number'] || ''

  async function beginFromOverlay(bs) {
    // Optimistic local flag so the overlay doesn't re-pop while the PATCH lands.
    entry.data.project.fields[bs ? 'BS Onboarding Acknowledged' : 'Onboarding Acknowledged'] = true
    forceUpdate()
    setModalOpen(true)
    acknowledgeOnboarding(projectId, { bs }).catch(() => {})
  }

  const sectionLabel = st.needBSCycle
    ? `Section ${Math.min(st.bsActiveSection, st.bsTotalSections)} of ${st.bsTotalSections}`
    : `Section ${Math.min(st.activeSection, st.totalSections)} of ${st.totalSections}`

  return (
    <>
      {(showFirstOverlay || showBSOverlay) && createPortal(
        <div style={S.ovRoot}>
          <div style={S.ovBackdrop} />
          <div style={S.ovPanel}>
            <div style={S.ovHead}>
              <div style={S.ovEyebrow}>{showBSOverlay ? 'Basic Services Activated' : 'Project Ready'}</div>
              <div style={S.ovTitle}>{projName}</div>
              <div style={S.ovNum}>{projNum}</div>
            </div>
            <div style={S.ovBody}>
              <div style={S.ovSub}>{showBSOverlay ? 'Full Scope Confirmed' : 'Onboarding tasks are ready'}</div>
              <div style={S.ovMsg}>
                {showBSOverlay
                  ? 'This project has advanced to Basic Services. The initial Design Consultation phase is complete. A new set of onboarding tasks is ready to complete the Core setup for the full project scope.'
                  : 'This project’s onboarding checklist is live. Complete the steps to finish setting up this project in BQE Core.'}
              </div>
              <button style={S.ovBtn} onClick={() => beginFromOverlay(showBSOverlay)}>
                {showBSOverlay ? 'Begin BS Onboarding' : 'Begin Onboarding'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {!modalOpen && !(showFirstOverlay || showBSOverlay) && createPortal(
        <button style={S.pill} onClick={() => setModalOpen(true)} title="Open the project onboarding checklist">
          <span style={S.pillDot} />
          <span style={S.pillLabel}>{st.needBSCycle ? 'BS Onboarding' : 'Project Onboarding'}</span>
          <span style={S.pillMeta}>{sectionLabel}</span>
        </button>,
        document.body
      )}

      {modalOpen && (
        <OnboardingModal bundle={entry.data} st={st} projectId={projectId}
          onClose={() => setModalOpen(false)} onChanged={refresh} forceUpdate={forceUpdate} />
      )}
    </>
  )
}

/* ── Wizard modal ─────────────────────────────────────────────────────────── */

function OnboardingModal({ bundle, st, projectId, onClose, onChanged, forceUpdate }) {
  const [members, setMembers] = useState(null)
  const [busy, setBusy] = useState(false)
  const [expanded, setExpanded] = useState(null)   // manually-toggled section num, or null = active only
  const [retainerHint, setRetainerHint] = useState('')
  const loadedRef = useRef(false)

  const onbF = st.onbF
  const pf = st.pf
  const of_ = bundle.org?.fields || {}
  const cf = bundle.contact?.fields || {}

  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    getTeamMembers().then(setMembers).catch(() => setMembers([]))
  }, [])

  // Retainer hint — legacy showed "(≈ $X)" next to the verify-amount item, 10% of the executed
  // package fee. Resolved from the executed proposal's own Selected Package (an improvement over
  // the legacy B||A||C guess). Best-effort; label renders without it.
  const needHint = (st.secondCycle ? st.needBSRetainer : st.needRetainer)
  useEffect(() => {
    if (!needHint) return
    const propId = first(onbF['Record ID (from Executed Proposal) (from Project)'])
    if (!propId) return
    getProposalDetail(propId).then(p => {
      if (!p) return
      const pkgKey = { 'Essentials': 'A', 'Premier': 'B', 'Premier Architecture': 'B', 'Architecture + Interiors': 'C' }[p.selectedPackage]
      const fee = pkgKey ? p.packages[pkgKey] : (p.contractFee || 0)
      if (fee) setRetainerHint(money(Math.round(fee * 0.10)))
    }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needHint])

  /* ---- Shared field data (ported shortcuts) ---- */
  const projName = pf['Project Name'] || ''
  const projNum = pf['Project Number'] || ''
  const projNumName = first(onbF['Number and Name (from Project)']) || (projNum + ' ' + projName).trim()
  const clientName = of_['Organization Name'] || first(onbF['Client Name (from Project)']) || ''
  const street = pf['Project Street Address'] || ''
  const cityLine = [pf['Project City'], pf['Project State']].filter(Boolean).join(', ') + (pf['Project Zip'] ? ' ' + pf['Project Zip'] : '')
  const fullAddr = [street, cityLine].filter(Boolean).join('\n')
  const agreeDate = pf['Agreement Date'] || ''
  const agreementUrl = pf['Signed DC Agreement URL'] || pf['Signed Agreement URL'] || pf['Executed Proposal URL'] || ''
  const bsAgreementUrl = pf['Signed BS Agreement URL'] || pf['Signed Agreement URL'] || ''
  const contactName = [cf['First Name'], cf['Last Name']].filter(Boolean).join(' ')
    || [first(pf['Primary Contact First Name (from Client)']), first(pf['Primary Contact Last Name (from Client)'])].filter(Boolean).join(' ')
  const contactFirst = cf['First Name'] || first(pf['Primary Contact First Name (from Client)']) || contactName
  const contactPhone = cf['Phone'] || ''
  const contactEmail = cf['Email'] || ''
  const clientPhone = pf['Client Phone'] || of_['Phone'] || ''
  const clientEmail = of_['General Email'] || of_['Email'] || contactEmail
  const bqeClientId = of_['BQE Core ID'] || ''
  const coreUrl = pf['Core Project UID'] ? (pf['Open in Core'] || '') : ''
  const scaffoldStatus = st.secondCycle || st.bsOnlyMode ? (onbF['BS Setup Status'] || onbF['Core Setup Status'] || '') : (onbF['Core Setup Status'] || '')
  const scaffoldFailed = /^Failed/i.test(scaffoldStatus)

  /* ---- Write helpers ---- */

  function patchLocal(fields) {
    Object.assign(bundle.onboarding.fields, fields)
    forceUpdate()
  }

  async function setField(fields) {
    if (!st.onbId) return
    patchLocal(fields)
    try { await updateOnboarding(st.onbId, fields) } catch (e) {
      await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save.' })
      onChanged()
    }
  }

  /** Section-advancing action: confirm → patch → refetch. Ported from makeActionBtn. */
  async function action({ title, message, fields, after }) {
    if (busy || !st.onbId) return
    if (!(await confirmDialog({ title, message, confirmLabel: 'Confirm' }))) return
    setBusy(true)
    try {
      await updateOnboarding(st.onbId, fields)
      if (after) await after()
      setExpanded(null)
      onChanged()
    } catch (e) {
      await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save.' })
    } finally { setBusy(false) }
  }

  /** Confirm Setup — the one action with a side effect beyond Airtable: it locks the setup
   *  selections AND runs the Core scaffold. Ported behavior: flag first, then scaffold. On
   *  scaffold failure the endpoint has already rolled back its own creations and written a
   *  Failed status; we surface the error and offer Retry from the verify section. */
  async function confirmSetup() {
    if (busy || !st.onbId) return
    const flagField = (st.secondCycle || st.bsOnlyMode) ? 'BS Ready for Core' : 'Ready for Core Phases'
    const retLabel = (st.secondCycle || st.bsOnlyMode) ? 'BS Retainer Required' : 'DC Retainer Required'
    const ok = await confirmDialog({
      title: 'Confirm Setup',
      message: st.secondCycle
        ? 'This will update the BQE parent project to Fixed Fee, create all BS phases and milestones, and lock the retainer selection. This cannot be undone.'
        : `This will lock your setup selections and run the Core setup automation (BQE client if new, project, phases, and milestones). Existing Client, ${retLabel} and Client Manager cannot be changed after this point.`,
      confirmLabel: 'Confirm Setup',
    })
    if (!ok) return
    setBusy(true)
    try {
      await updateOnboarding(st.onbId, { [flagField]: true })
      await scaffoldProject(projectId)
      onChanged()
    } catch (e) {
      onChanged()   // pick up the endpoint's Failed status + any rollback
      await alertDialog({ title: 'Core setup failed', message: (e && e.message) || 'The scaffold did not complete. Use Retry in the verify section once the issue is resolved.' })
    } finally { setBusy(false) }
  }

  async function retryScaffold() {
    if (busy) return
    if (!(await confirmDialog({ title: 'Retry Core setup?', message: 'Re-runs the Core scaffold. The previous failed attempt rolled back its own creations, so this is safe to retry.', confirmLabel: 'Retry' }))) return
    setBusy(true)
    try { await scaffoldProject(projectId); onChanged() }
    catch (e) { onChanged(); await alertDialog({ title: 'Core setup failed', message: (e && e.message) || 'Still failing — check the onboarding record’s setup status for the log tail.' }) }
    finally { setBusy(false) }
  }

  async function complete() {
    const cycle = (st.secondCycle || st.bsOnlyMode) ? 'bs' : 'dc'
    if (busy || !st.onbId) return
    if (!(await confirmDialog({
      title: 'Complete Onboarding',
      message: 'This will mark the project as fully onboarded. All steps will be locked. This cannot be undone.',
      confirmLabel: 'Complete', danger: true,
    }))) return
    setBusy(true)
    try {
      await completeOnboarding({
        onbId: st.onbId, projectId, cycle, secondCycle: st.secondCycle,
        memberId: ctx.currentMember && ctx.currentMember.id,
      })
      onChanged()
      await alertDialog({ title: 'Onboarding complete', message: cycle === 'dc' ? 'DC onboarding is complete. The project is ready to advance to Basic Services.' : 'The project is fully onboarded and now in production.' })
      onClose()
    } catch (e) {
      await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not complete onboarding.' })
    } finally { setBusy(false) }
  }

  /* ---- Section assembly ---- */

  const sections = []
  const activeSection = st.secondCycle ? st.bsActiveSection : st.activeSection
  const isBS = st.secondCycle || st.bsOnlyMode   // which field family Setup/Verify/Retainer use

  // 1 — Setup
  sections.push({
    num: 1,
    title: 'Setup',
    body: (isActive) => (
      <SetupSection st={st} onbF={onbF} members={members} isActive={isActive} busy={busy}
        secondCycle={st.secondCycle} setField={setField} confirmSetup={confirmSetup} />
    ),
  })

  // 2 — Verify New Client in Core (first cycle only, new clients only — runs on BOTH tracks;
  // see the deriveState comment on why bsOnlyMode no longer excludes this)
  if (!st.secondCycle && !st.isExisting) {
    sections.push({
      num: 2,
      title: 'Verify New Client in Core',
      body: (isActive) => (
        <VerifySection
          isActive={isActive} busy={busy} done={st.clientDone}
          link={bqeClientId ? { href: 'https://wap1-01.bqecore.com/webapp/Client/Edit/' + bqeClientId, label: '↗ Open Client in BQE Core' } : null}
          infoGroups={[
            { label: 'Client Information', rows: [['Name', clientName ? clientName + ' (Organization)' : ''], ['Main Contact', contactName], ['Address', fullAddr], ['Main Email', clientEmail], ['Main Phone', clientPhone]] },
            { label: 'Primary Contact', rows: [['Name', contactName], ['Phone', contactPhone], ['Email', contactEmail]] },
          ]}
          items={CLIENT_ITEMS} stateField="Client Section State" stateJson={onbF['Client Section State']}
          onbId={st.onbId} patchLocal={patchLocal}
          buttonLabel="Client & Contact Added"
          onAction={() => action({
            title: 'Confirm Client and Contact',
            message: 'This marks the client and contact as verified in BQE Core and advances to project verification.',
            fields: { 'Add Client to BQE Core': true, 'Add Contact to Client': true },
          })}
        />
      ),
    })
  }

  // Verify project/phases
  const verifyNum = st.secondCycle ? 2 : (st.isExisting ? 2 : 3)
  const verifyItems = st.secondCycle ? BS_PROJ_ITEMS : PROJ_ITEMS
  const verifyStateField = isBS && st.secondCycle ? 'BS Project Section State' : (st.bsOnlyMode ? 'BS Project Section State' : 'Project Section State')
  // BS-track sections need the signed BS agreement (bsAgreementUrl), not the DC one —
  // agreementUrl's fallback chain leads with 'Signed DC Agreement URL', which is blank on a
  // BS-direct or DC→BS-executed project, so the link silently never rendered on this section.
  sections.push({
    num: verifyNum,
    title: isBS ? 'Verify BS Phases in Core' : 'Verify Project in Core',
    body: (isActive) => (
      <VerifySection
        isActive={isActive} busy={busy} done={st.secondCycle ? st.bsProjDone : st.projDone}
        link={coreUrl ? { href: coreUrl, label: '↗ Open Project in BQE Core' } : null}
        scaffoldStatus={scaffoldStatus} scaffoldFailed={scaffoldFailed} onRetry={retryScaffold}
        infoGroups={st.secondCycle ? [] : [
          { label: 'Project Information', rows: [['Project ID', projNum], ['Name', projName], ['Client', clientName], ['Address', fullAddr], ['Start Date', agreeDate]] },
        ]}
        agreementLink={(isBS ? bsAgreementUrl : agreementUrl) || null}
        items={verifyItems} stateField={verifyStateField} stateJson={onbF[verifyStateField]}
        onbId={st.onbId} patchLocal={patchLocal}
        buttonLabel={isBS ? 'BS Phases Verified' : 'Project Verified'}
        onAction={() => action({
          title: isBS ? 'Confirm Phase Verification' : 'Confirm Project',
          message: isBS ? 'This marks the BS phases and fee structure as verified in BQE Core.' : 'This marks the project as verified in BQE Core and advances to the next step.',
          fields: isBS ? { 'BS Phases Verified': true } : { 'Add Project to BQE Core': true },
        })}
      />
    ),
  })

  // Retainer (conditional)
  const wantRetainer = st.secondCycle ? st.needBSRetainer : st.needRetainer
  if (wantRetainer) {
    const retNum = st.secondCycle ? 3 : (st.isExisting ? 3 : 4)
    sections.push({
      num: retNum,
      title: isBS ? 'BS Send Retainer Request' : 'DC Send Retainer Request',
      body: (isActive) => (
        <RetainerSection
          isActive={isActive} busy={busy}
          done={st.secondCycle ? st.bsRetainerDone : st.retainerDone}
          secondCycle={st.secondCycle}
          agreementUrl={bsAgreementUrl} clientName={clientName} projNumName={projNumName}
          contactFirst={contactFirst} retainerHint={retainerHint}
          stateJson={onbF['Retainer Section State']} onbId={st.onbId} patchLocal={patchLocal}
          onAction={() => action({
            title: 'Confirm Retainer',
            message: isBS ? 'This marks the BS retainer request as sent and advances to the final step.' : 'This marks the retainer request as sent and advances to the final step.',
            fields: isBS ? { 'BS Send Retainer Request': true } : { 'DC Send Retainer Request': true },
          })}
        />
      ),
    })
  }

  // Complete
  const lastNum = st.secondCycle
    ? (st.needBSRetainer ? 4 : 3)
    : st.totalSections
  sections.push({
    num: lastNum,
    title: st.secondCycle ? 'Complete BS Onboarding' : 'Complete Onboarding',
    body: (isActive) => (
      <div>
        <div style={S.inst}>
          <strong>Before completing</strong>: please review all previous steps to ensure they have been
          completed accurately, including client details, project setup{wantRetainer ? ', and the retainer request' : ''}.
          Once onboarding is completed, changes to these items cannot be made and the project will move forward as-is.
        </div>
        <ActionBtn label="Mark Onboarding Complete" disabled={!isActive || busy} busy={isActive && busy} onClick={complete} />
      </div>
    ),
  })

  const totalSections = st.secondCycle ? st.bsTotalSections : st.totalSections

  return createPortal(
    <div style={S.mRoot}>
      <div style={S.mScrim} onClick={onClose} />
      <div style={S.mPanel}>
        <div style={S.mHead}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.mTitle}>{st.secondCycle ? 'Basic Services Onboarding' : 'Project Onboarding'}</div>
            <div style={S.mSub}>{projNumName}</div>
          </div>
          <span style={S.mProgress}>Section {Math.min(activeSection, totalSections)} of {totalSections}</span>
          <button style={S.mClose} onClick={onClose} title="Close">✕</button>
        </div>

        {st.secondCycle && (
          <div style={S.bsNotice}>
            <strong>Continuing to Full Scope</strong>: This project has advanced from Design Consultation to
            Basic Services. Complete the steps below to finish Core setup for the full project scope.
          </div>
        )}
        {st.secondCycle && !!onbF['Onboarding Complete'] && (
          <HistoryRow title="DC Onboarding" items={[
            ['Existing Client', onbF['Existing Client'] || '--'],
            ['DC Retainer Required', onbF['DC Retainer Required'] || '--'],
            ['Client Added to Core', onbF['Add Client to BQE Core'] ? 'Done' : '--'],
            ['Project Verified in Core', onbF['Add Project to BQE Core'] ? 'Done' : '--'],
            ['DC Retainer Request Sent', onbF['DC Send Retainer Request'] ? 'Done' : 'N/A'],
          ]} />
        )}

        <div style={S.mBody}>
          {sections.map(sec => {
            const isDone = sec.num < activeSection
            const isActive = sec.num === activeSection
            const open = expanded === sec.num || (expanded === null && isActive)
            return (
              <div key={sec.num} style={S.sec}>
                <button style={S.secHd} onClick={() => setExpanded(open ? (isActive ? -1 : null) : sec.num)}>
                  <span style={{ ...S.secNum, ...(isDone ? S.secNumDone : isActive ? S.secNumActive : null) }}>
                    {isDone ? '✓' : sec.num}
                  </span>
                  <span style={{ ...S.secTitle, ...(isDone ? S.secTitleDone : sec.num > activeSection ? S.secTitleUpcoming : null) }}>{sec.title}</span>
                  <span style={{ ...S.secChev, transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
                </button>
                {open && <div style={S.secBody}>{sec.body(isActive)}</div>}
              </div>
            )
          })}
        </div>
      </div>
    </div>,
    document.body
  )
}

/* ── Section internals ────────────────────────────────────────────────────── */

// Sub-checklist item sets — keys must stay identical to the legacy JSON state blobs.
const CLIENT_ITEMS = [
  { key: 'client_record_created', label: 'Client record created with correct name' },
  { key: 'client_phone', label: 'Phone number populated at client level' },
  { key: 'client_email', label: 'Email populated at client level' },
  { key: 'contact_record', label: 'Primary contact record created' },
  { key: 'contact_phone', label: 'Phone number populated on contact' },
  { key: 'contact_email', label: 'Email populated on contact' },
  { key: 'contact_set_primary', label: 'Set as Primary checked on contact', note: 'Found under Display As inputs on the contact record' },
  { key: 'additional_contacts', label: 'Additional contacts added if needed', note: 'Additional contacts can be copied on invoices' },
]
const PROJ_ITEMS = [
  { key: 'proj_id', label: 'Project ID matches' },
  { key: 'proj_name', label: 'Project name is correct' },
  { key: 'proj_client', label: 'Client is linked correctly' },
  { key: 'proj_address', label: 'Project address is accurate' },
  { key: 'proj_start_date', label: 'Start date matches agreement date' },
  { key: 'proj_phases', label: 'All phases came in from template' },
  { key: 'proj_phases_active', label: 'Only relevant phases activated' },
  { key: 'proj_phase_type', label: 'Each phase: contract type set correctly' },
  { key: 'proj_phase_amount', label: 'Each phase: contract amount matches proposal' },
  { key: 'proj_principal', label: 'Principal assigned at parent project level', note: 'Settings cascade down to all phases' },
  { key: 'proj_fee_schedule', label: 'Fee schedule set correctly' },
  { key: 'proj_auto_invoice', label: 'Automatic invoice numbering enabled' },
  { key: 'proj_billing_primary', label: 'Primary billing contact set' },
  { key: 'proj_billing_extra', label: 'Additional billing contacts added if needed' },
  { key: 'proj_payment_online', label: 'Online payment account set', note: 'ACH is our standard accepted form' },
  { key: 'proj_payment_terms', label: 'Payment terms set to Due Upon Receipt' },
]
const BS_PROJ_ITEMS = [
  { key: 'bs_parent_fee', label: 'Parent project updated to Fixed Fee with correct BS contract amount' },
  { key: 'bs_parent_type', label: 'Parent project contract type changed from Hourly to Fixed Fee' },
  { key: 'bs_phases_exist', label: 'All BS phases came in correctly' },
  { key: 'bs_phase_fees', label: 'Each BS phase has the correct contracted fee amount' },
  { key: 'bs_phase_type', label: 'Each BS phase contract type is set correctly' },
  { key: 'bs_phase_active', label: 'Only relevant phases are activated' },
  { key: 'bs_milestones', label: 'Milestones scaffolded correctly on each phase' },
  { key: 'bs_billing', label: 'Billing contact and payment terms confirmed' },
]

function SetupSection({ st, onbF, members, isActive, busy, secondCycle, setField, confirmSetup }) {
  const bsFields = secondCycle || st.bsOnlyMode
  const retainerField = bsFields ? 'BS Retainer Required' : 'DC Retainer Required'
  const ready = secondCycle ? st.bsReady : st.readyForCore
  const cmVal = first(onbF['Client Manager']) || ''

  // Second cycle has no Existing Client / Client Manager rows (ported — already set in cycle 1).
  const showFullSetup = !secondCycle

  const existingSet = onbF['Existing Client'] === 'Yes' || onbF['Existing Client'] === 'No'
  const retainerSet = onbF[retainerField] === 'Yes' || onbF[retainerField] === 'No'
  const canConfirm = secondCycle ? retainerSet : (existingSet && retainerSet && !!cmVal)

  return (
    <div>
      {showFullSetup && (
        <YnRow label="Existing Client" value={onbF['Existing Client']} locked={ready}
          onPick={v => setField({ 'Existing Client': v })} />
      )}
      <YnRow label={retainerField} value={onbF[retainerField]} locked={ready}
        onPick={v => setField({ [retainerField]: v })} />
      {showFullSetup && (
        <div style={S.frow}>
          <div style={S.flbl}>Client Manager</div>
          <select style={S.select} disabled={ready || members === null} value={cmVal}
            onChange={e => setField({ 'Client Manager': e.target.value ? [e.target.value] : [] })}>
            <option value="">{members === null ? 'Loading…' : 'Select…'}</option>
            {(members || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      )}
      {secondCycle && (
        <div style={S.inst}>
          Clicking Confirm Setup will update the parent project in BQE Core to Fixed Fee, create all
          Basic Services phases, and scaffold milestones. This will also lock the retainer selection above.
        </div>
      )}
      {!ready
        ? <ActionBtn label="Confirm Setup" disabled={!isActive || busy || !canConfirm} busy={isActive && busy} onClick={confirmSetup} />
        : <div style={S.doneNote}>
            {secondCycle || st.bsOnlyMode
              ? 'Setup confirmed. Core project and BS phases have been created.'
              : 'Setup confirmed. Core project and DC phase have been created.'}
          </div>}
    </div>
  )
}

function VerifySection({ isActive, busy, done, link, infoGroups = [], agreementLink, items, stateField, stateJson, onbId, patchLocal, buttonLabel, onAction, scaffoldStatus, scaffoldFailed, onRetry }) {
  const [state, setState] = useState(() => { try { return JSON.parse(stateJson || '{}') } catch { return {} } })
  const allChecked = items.every(it => !!state[it.key])

  function toggle(key) {
    if (!isActive || done) return
    const next = { ...state, [key]: !state[key] }
    setState(next)
    patchLocal({ [stateField]: JSON.stringify(next) })
    if (onbId) updateOnboarding(onbId, { [stateField]: JSON.stringify(next) }).catch(() => {})
  }

  return (
    <div>
      {link && <a href={link.href} target="_blank" rel="noopener noreferrer" style={S.coreLink}>{link.label}</a>}
      {agreementLink && <a href={agreementLink} target="_blank" rel="noopener noreferrer" style={S.coreLink}>↗ View Signed Agreement</a>}
      {scaffoldStatus && (
        <div style={{ ...S.scaffoldStatus, ...(scaffoldFailed ? S.scaffoldStatusFail : null) }}>
          Core setup status: {scaffoldStatus}
          {scaffoldFailed && (
            <button style={S.retryBtn} onClick={onRetry} disabled={busy}>
              {busy ? <span style={S.actionBtnBusy}><span style={S.spinnerDark} />Working…</span> : 'Retry Core setup'}
            </button>
          )}
        </div>
      )}
      {infoGroups.map(g => (
        <div key={g.label}>
          <div style={S.divider}>{g.label}</div>
          {g.rows.map(([l, v]) => <InfoRow key={l} label={l} value={v} />)}
        </div>
      ))}
      <div style={S.divider}>Verification Checklist</div>
      {items.map(it => (
        <CheckRow key={it.key} item={it} checked={!!state[it.key]} locked={!isActive || done} onToggle={() => toggle(it.key)} />
      ))}
      <ActionBtn label={buttonLabel} disabled={!isActive || done || busy || !allChecked} busy={isActive && busy} onClick={onAction} />
    </div>
  )
}

function RetainerSection({ isActive, busy, done, secondCycle, agreementUrl, clientName, projNumName, contactFirst, retainerHint, stateJson, onbId, patchLocal, onAction }) {
  const items = [
    { key: 'ret_overall_fee', label: 'Overall project fee set at parent project level', note: 'This amount appears on the initial payment invoice' },
    { key: 'ret_verify_amount', label: 'Retainer amount verified against signed agreement' + (retainerHint ? ` (≈ ${retainerHint})` : '') },
  ]
  const [state, setState] = useState(() => { try { return JSON.parse(stateJson || '{}') } catch { return {} } })
  // Ported quirk: the second cycle's retainer section has no gating checklist — its button is
  // gated only on being the active section.
  const allChecked = secondCycle ? true : items.every(it => !!state[it.key])

  function toggle(key) {
    if (!isActive || done) return
    const next = { ...state, [key]: !state[key] }
    setState(next)
    patchLocal({ 'Retainer Section State': JSON.stringify(next) })
    if (onbId) updateOnboarding(onbId, { 'Retainer Section State': JSON.stringify(next) }).catch(() => {})
  }

  const emailSubject = secondCycle
    ? `Request for Initial Payment (Basic Services): ${projNumName} (Alder James Architecture)`
    : `Request for Initial Payment - ${projNumName} (Alder James Architecture)`
  const emailBody = secondCycle
    ? `${contactFirst || 'Hello'},\n\nThank you for completing the Design Consultation phase of your project. We are pleased to begin the full Basic Services scope and have attached a request for initial payment.\n\nIf you have any questions, please let us know.\n\nThank you.`
    : `${contactFirst || 'Hello'},\n\nPlease find the attached request for initial payment to begin work on your project. If you have any questions, please let us know.\n\nThank you.`

  return (
    <div>
      {agreementUrl && <a href={agreementUrl} target="_blank" rel="noopener noreferrer" style={S.coreLink}>↗ View Signed Agreement</a>}
      {secondCycle
        ? <div style={S.inst}>A second retainer request is required for Basic Services. Note that the client has already paid a DC retainer; the communication should acknowledge that context.</div>
        : <>
            <div style={S.divider}>Before Sending</div>
            {items.map(it => (
              <CheckRow key={it.key} item={it} checked={!!state[it.key]} locked={!isActive || done} onToggle={() => toggle(it.key)} />
            ))}
          </>}
      <div style={S.divider}>Steps in BQE Core</div>
      <ol style={S.steps}>
        <li>Go to <strong>Billing</strong> in the side menu and click <strong>Retainers</strong></li>
        <li>Set the Client to <strong>{clientName}</strong> and the Project to <strong>{projNumName}</strong></li>
        <li>Click <strong>Request Retainer</strong> and enter the amount</li>
        <li>Set the online payment account as appropriate — ACH is our standard accepted form</li>
        <li>Click the caret next to the new line item and select <strong>Email</strong></li>
        <li>Copy the subject and body below into the email dialog{secondCycle ? ', adjusted to acknowledge the prior DC retainer' : ''}</li>
        <li>Review carefully and click <strong>Send Message</strong></li>
      </ol>
      <div style={S.divider}>Email Content</div>
      <CopyBox label="Email Subject" value={emailSubject} />
      <CopyBox label="Email Body" value={emailBody} />
      <ActionBtn label={secondCycle ? 'BS Retainer Request Sent' : 'Retainer Request Sent'} disabled={!isActive || done || busy || !allChecked} busy={isActive && busy} onClick={onAction} />
    </div>
  )
}

/* ── Small building blocks ────────────────────────────────────────────────── */

function YnRow({ label, value, locked, onPick }) {
  return (
    <div style={S.frow}>
      <div style={S.flbl}>{label}</div>
      <div style={{ display: 'flex', gap: 6 }}>
        {['Yes', 'No'].map(opt => (
          <button key={opt} disabled={locked}
            style={{ ...S.yn, ...(value === opt ? S.ynSelected : null), ...(locked ? { opacity: 0.55, cursor: 'not-allowed' } : null) }}
            onClick={() => !locked && onPick(opt)}>{opt}</button>
        ))}
      </div>
    </div>
  )
}

function CheckRow({ item, checked, locked, onToggle }) {
  return (
    <div style={S.ckRow}>
      <button
        style={{ ...S.ck, ...(checked ? S.ckOn : null), ...(locked ? { cursor: 'not-allowed', opacity: 0.45 } : null) }}
        onClick={onToggle} disabled={locked} aria-checked={checked} role="checkbox">
        {checked ? '✓' : ''}
      </button>
      <div style={{ flex: 1 }}>
        <div style={{ ...S.ckLabel, ...(checked ? S.ckLabelDone : null) }}>{item.label}</div>
        {item.note && <div style={S.ckNote}>{item.note}</div>}
      </div>
    </div>
  )
}

function ActionBtn({ label, disabled, busy, onClick }) {
  return (
    <div style={{ marginTop: 14 }}>
      <button style={{ ...S.actionBtn, ...(disabled ? { opacity: 0.4, cursor: 'not-allowed' } : null) }}
        disabled={disabled} onClick={onClick}>
        {busy ? <span style={S.actionBtnBusy}><span style={S.spinner} />Working…</span> : `${label} →`}
      </button>
    </div>
  )
}

// One label/value line. Clicking the value copies it for pasting into Core; a phone copies as digits
// only, since Core's phone field refuses "(928) 303-5966" (Matt, 2026-10-29).
function InfoRow({ label, value }) {
  const [copied, setCopied] = useState(false)
  if (!value) return (
    <div style={S.infoRow}>
      <div style={S.infoLbl}>{label}</div>
      <div style={S.infoVal}><span style={{ color: 'var(--aj-text-muted)' }}>—</span></div>
    </div>
  )
  const isPhone = /phone/i.test(label)
  const text = isPhone ? String(value).replace(/\D/g, '') : String(value)
  return (
    <div style={S.infoRow}>
      <div style={S.infoLbl}>{label}</div>
      <div style={{ ...S.infoVal, cursor: 'pointer', position: 'relative' }} title={isPhone ? 'Click to copy as ' + text : 'Click to copy'} onClick={() => {
        if (navigator.clipboard) navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) })
      }}>
        {value}
        {copied && <span style={S.copied}>{isPhone ? 'Copied ' + text : 'Copied'}</span>}
      </div>
    </div>
  )
}

function CopyBox({ label, value }) {
  const [copied, setCopied] = useState(false)
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={S.copyLbl}>{label}</div>
      <div style={S.copyBox} title="Click to copy" onClick={() => {
        if (navigator.clipboard) navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) })
      }}>
        {value}
        {copied && <span style={S.copied}>Copied</span>}
      </div>
    </div>
  )
}

function HistoryRow({ title, items }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ borderBottom: '0.5px solid var(--aj-border)' }}>
      <button style={S.secHd} onClick={() => setOpen(o => !o)}>
        <span style={{ ...S.secNum, ...S.secNumDone }}>✓</span>
        <span style={{ ...S.secTitle, ...S.secTitleDone }}>{title}</span>
        <span style={{ ...S.secChev, transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </button>
      {open && (
        <div style={{ ...S.secBody, paddingLeft: 50 }}>
          {items.map(([l, v]) => (
            <div key={l} style={S.infoRow}>
              <div style={S.infoLbl}>{l}</div>
              <div style={S.infoVal}>{v}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Styles (design-system tokens; DM Sans UI, Montserrat display, DM Mono numbers) ── */

const S = {
  // Overlay
  ovRoot: { position: 'fixed', inset: 0, zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  ovBackdrop: { position: 'absolute', inset: 0, background: 'rgba(7,39,45,0.65)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' },
  ovPanel: { position: 'relative', zIndex: 1, background: 'var(--aj-bg-elevated)', borderRadius: 16, width: '100%', maxWidth: 440, boxShadow: '0 24px 80px rgba(7,39,45,0.3)', overflow: 'hidden' },
  ovHead: { background: '#07272D', backgroundImage: 'radial-gradient(ellipse at 15% 60%, rgba(15,80,90,0.5) 0%, transparent 55%), radial-gradient(ellipse at 85% 20%, rgba(10,55,70,0.35) 0%, transparent 50%)', padding: '22px 24px 20px' },
  ovEyebrow: { fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 6 },
  ovTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 20, fontWeight: 300, letterSpacing: '0.05em', color: '#fff', textTransform: 'uppercase', lineHeight: 1.2 },
  ovNum: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 },
  ovBody: { padding: 24 },
  ovSub: { fontFamily: '"Montserrat", sans-serif', fontSize: 12, fontWeight: 500, color: 'var(--aj-text)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
  ovMsg: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', lineHeight: 1.7, marginBottom: 20 },
  ovBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '11px 24px', background: '#07272D', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, fontFamily: '"DM Sans", sans-serif', cursor: 'pointer' },

  // Pill
  // z 390: deliberately below the full-bleed workspaces (400) and SiteNav (500) — see wsOpen note.
  pill: { position: 'fixed', bottom: 18, right: 18, zIndex: 390, display: 'inline-flex', alignItems: 'center', gap: 9, padding: '10px 16px', background: '#07272D', color: '#fff', border: 'none', borderRadius: 20, cursor: 'pointer', boxShadow: '0 8px 24px rgba(7,39,45,0.35)', fontFamily: '"DM Sans", sans-serif' },
  pillDot: { width: 7, height: 7, borderRadius: '50%', background: '#f5b942', flexShrink: 0 },
  pillLabel: { fontSize: 12.5, fontWeight: 600 },
  pillMeta: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: 'rgba(255,255,255,0.55)' },

  // Modal
  mRoot: { position: 'fixed', inset: 0, zIndex: 920, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  mScrim: { position: 'absolute', inset: 0, background: 'rgba(7,39,45,0.45)' },
  mPanel: { position: 'relative', zIndex: 1, background: 'var(--aj-bg-elevated)', borderRadius: 10, width: '100%', maxWidth: 680, maxHeight: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 80px rgba(7,39,45,0.3)' },
  mHead: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)' },
  mTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 14, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  mSub: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 },
  mProgress: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-mid)', flexShrink: 0 },
  mClose: { flexShrink: 0, background: 'none', border: 'none', fontSize: 15, color: 'var(--aj-text-mid)', cursor: 'pointer', padding: '2px 6px' },
  mBody: { overflowY: 'auto', padding: '4px 0 16px' },
  bsNotice: { background: 'rgba(122,90,248,0.08)', borderBottom: '0.5px solid var(--aj-border)', padding: '10px 20px', fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', lineHeight: 1.5 },

  // Sections
  sec: { borderBottom: '0.5px solid var(--aj-border)' },
  secHd: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '13px 20px', cursor: 'pointer' },
  secNum: { width: 22, height: 22, borderRadius: '50%', border: '1.5px solid var(--aj-border-mid)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-muted)', flexShrink: 0 },
  secNumActive: { border: '1.5px solid #07272D', background: '#07272D', color: '#fff' },
  secNumDone: { border: '1.5px solid #1f9d57', background: '#1f9d57', color: '#fff' },
  secTitle: { flex: 1, fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, fontWeight: 600, color: 'var(--aj-text)' },
  secTitleDone: { color: 'var(--aj-text-muted)', fontWeight: 500 },
  secTitleUpcoming: { color: 'var(--aj-text-muted)', fontWeight: 500 },
  secChev: { fontSize: 11, color: 'var(--aj-text-muted)', transition: 'transform 0.2s' },
  secBody: { padding: '2px 20px 18px 54px' },

  // Fields
  frow: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '0.5px solid var(--aj-border)' },
  flbl: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', minWidth: 160, flexShrink: 0 },
  yn: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-muted)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 16, padding: '5px 16px', cursor: 'pointer' },
  // One consistent "selected" treatment regardless of whether Yes or No is picked — previously
  // Yes-selected and No-selected used two different styles (dark-filled vs light/bold-text),
  // which read as two different kinds of state instead of one selection indicator.
  ynSelected: { background: '#07272D', borderColor: '#07272D', color: '#fff', fontWeight: 600 },
  select: { flex: 1, maxWidth: 260, fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', padding: '6px 8px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)' },

  // Info + checklist
  divider: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, color: 'var(--aj-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '14px 0 6px', borderBottom: '0.5px solid var(--aj-border)', marginBottom: 2 },
  infoRow: { display: 'flex', gap: 10, padding: '6px 0', borderBottom: '0.5px solid var(--aj-border)' },
  infoLbl: { fontFamily: '"Montserrat", sans-serif', fontSize: 10.5, fontWeight: 500, color: 'var(--aj-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 120, flexShrink: 0, paddingTop: 1 },
  infoVal: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  ckRow: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', borderBottom: '0.5px solid var(--aj-border)' },
  ck: { width: 16, height: 16, borderRadius: 3, border: '1.5px solid var(--aj-border-mid)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2, cursor: 'pointer', fontSize: 10, lineHeight: 1, color: '#fff', padding: 0 },
  ckOn: { border: '1.5px solid #1f9d57', background: '#1f9d57' },
  ckLabel: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)' },
  ckLabelDone: { color: 'var(--aj-text-muted)', textDecoration: 'line-through' },
  ckNote: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2, lineHeight: 1.4 },

  // Links / status / actions
  coreLink: { display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 500, color: '#1a6fa8', textDecoration: 'none', padding: '6px 12px', border: '0.5px solid rgba(26,111,168,0.35)', borderRadius: 20, background: 'rgba(26,111,168,0.08)', marginBottom: 12 },
  aLink: { fontSize: 13, color: '#1a6fa8' },
  scaffoldStatus: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-subtle)', borderRadius: 4, padding: '8px 10px', margin: '0 0 10px', lineHeight: 1.5 },
  scaffoldStatusFail: { color: '#b3261e', background: 'rgba(179,38,30,0.07)' },
  retryBtn: { display: 'block', marginTop: 6, fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, fontWeight: 600, color: '#b3261e', background: 'none', border: '0.5px solid rgba(179,38,30,0.4)', borderRadius: 4, padding: '4px 10px', cursor: 'pointer' },
  actionBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 6, padding: '10px 20px', cursor: 'pointer' },
  actionBtnBusy: { display: 'inline-flex', alignItems: 'center', gap: 8 },
  spinner: { display: 'inline-block', width: 11, height: 11, border: '1.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'wf-spin 0.75s linear infinite', flexShrink: 0 },
  spinnerDark: { display: 'inline-block', width: 11, height: 11, border: '1.5px solid rgba(179,38,30,0.25)', borderTopColor: '#b3261e', borderRadius: '50%', animation: 'wf-spin 0.75s linear infinite', flexShrink: 0 },
  doneNote: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#1f9d57', marginTop: 10, fontWeight: 500 },
  inst: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-subtle)', borderRadius: 6, padding: '10px 12px', margin: '10px 0', lineHeight: 1.55 },
  steps: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', lineHeight: 1.8, margin: '6px 0 12px', paddingLeft: 18 },
  copyLbl: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, color: 'var(--aj-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 },
  copyBox: { position: 'relative', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '10px 12px', fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text)', lineHeight: 1.6, whiteSpace: 'pre-wrap', cursor: 'pointer' },
  copied: { position: 'absolute', top: 6, right: 8, fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500, color: '#1f9d57', textTransform: 'uppercase', letterSpacing: '0.05em' },
}
